import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Bot } from "lucide-react";
import { AuthHeader } from "@/components/AuthHeader";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import Logo from "@/components/Logo";

interface LoginProps {
  variant?: "customer" | "supplier";
}

export default function Login({ variant = "customer" }: LoginProps) {
  const { t } = useI18n();
  const { user, isReady } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"login" | "register" | "confirm">("login");
  const navigate = useNavigate();
  const emailRef = useRef<HTMLInputElement>(null);

  const isSupplier = variant === "supplier";
  const redirectTo = isSupplier ? "/supplier/dashboard" : "/";

  useEffect(() => {
    if (isReady && user) {
      if (isSupplier) {
        ensureSupplierAndRedirect(user.id, user.email || "");
      } else {
        navigate("/");
      }
    }
  }, [isReady, user]);

  useEffect(() => {
    emailRef.current?.focus();
  }, [mode]);

  const ensureSupplierAndRedirect = async (userId: string, userEmail: string) => {
    const { data } = await supabase
      .from("suppliers")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();

    if (!data) {
      const localPart = userEmail.split("@")[0];
      await supabase.from("suppliers").insert({
        user_id: userId,
        company_name: localPart,
        slug: localPart.toLowerCase(),
        contact_email: userEmail,
        filter_locked: true,
      });
    }

    navigate("/supplier/dashboard");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (mode === "login") {
      const { data: { session }, error } = await supabase.auth.signInWithPassword({ email, password });
      setLoading(false);
      if (error) {
        toast.error(t("auth.invalidCredentials"));
      } else if (session?.user) {
        if (isSupplier) {
          await ensureSupplierAndRedirect(session.user.id, session.user.email || email);
        } else {
          navigate("/");
        }
      }
    } else {
      // Register
      const { data, error } = await supabase.auth.signUp({ email, password });
      setLoading(false);
      if (error) {
        if (error.message?.includes("already registered")) {
          toast.error(t("auth.alreadyRegistered"));
          setMode("login");
        } else {
          toast.error(error.message);
        }
      } else if (data.user && !data.session) {
        // Email confirmation required
        setMode("confirm");
      } else if (data.user && data.session) {
        // Auto-confirmed
        if (isSupplier) {
          await ensureSupplierAndRedirect(data.user.id, data.user.email || email);
        } else {
          navigate("/");
        }
      }
    }
  };

  const handleGoogleLogin = async () => {
    const { error } = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + (isSupplier ? "/supplier/register" : ""),
    });
    if (error) {
      toast.error(error.message);
    }
  };

  if (mode === "confirm") {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AuthHeader backTo="/" infoKey="register.info" />
        <div className="flex-1 flex items-center justify-start sm:justify-center px-4 pt-6 sm:pt-0 pb-8 overflow-y-auto">
          <div className="w-full max-w-sm animate-fade-in text-center">
            <Link to="/" className="flex items-center justify-center gap-2 mb-6 sm:mb-8">
              <Logo size={32} />
              <span className="font-semibold text-xl text-foreground">cenik.ai</span>
            </Link>
            <h1 className="text-xl font-semibold text-foreground mb-2">{t("register.emailSent")}</h1>
            <p className="text-muted-foreground text-sm mb-6">{t("register.checkEmailDesc")}</p>
            <button
              onClick={() => { setMode("login"); }}
              className="inline-block px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium text-sm hover:opacity-90 transition-opacity"
            >
              {t("register.goToLogin")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AuthHeader backTo="/" infoKey={mode === "login" ? "login.info" : "register.info"} />
      <div className="flex-1 flex items-center justify-start sm:justify-center px-4 pt-6 sm:pt-0 pb-8 overflow-y-auto">
      <div className="w-full max-w-sm animate-fade-in">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6 sm:mb-8">
          <Logo size={32} />
          <span className="font-semibold text-xl text-foreground">cenik.ai</span>
        </Link>

        <h1 className="text-2xl font-semibold text-foreground text-center mb-6">
          {isSupplier
            ? t("supplier.chatbotHeadline")
            : mode === "login" ? t("login.title") : t("register.title")
          }
        </h1>

        <p className="text-sm text-muted-foreground text-center mb-4">
          {isSupplier
            ? t("supplier.chatbotDescription")
            : mode === "login" ? t("login.subtitle") : t("register.subtitle")
          }
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t("login.email")}</label>
            <input
              ref={emailRef}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
              placeholder={t("login.emailPlaceholder")}
              required
              autoFocus
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-foreground">{t("login.password")}</label>
              {mode === "login" && (
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  {t("login.forgotPassword")}
                </Link>
              )}
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring"
              placeholder={mode === "register" ? t("register.passwordPlaceholder") : t("login.passwordPlaceholder")}
              required
              minLength={mode === "register" ? 8 : undefined}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "..." : mode === "login" ? t("auth.continue") : t("register.submit")}
          </button>
        </form>

        <button
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="w-full text-center text-sm text-muted-foreground mt-3 hover:text-foreground transition-colors"
        >
          {mode === "login" ? t("auth.switchToRegister") : t("auth.switchToLogin")}
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center"><span className="bg-background px-3 text-xs text-muted-foreground">{t("login.or")}</span></div>
        </div>

        <button
          onClick={handleGoogleLogin}
          className="w-full py-2.5 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-accent transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          {t("login.google")}
        </button>

        {!isSupplier && (
          <div className="mt-6 border border-border rounded-xl px-4 py-3 text-xs text-muted-foreground space-y-2">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4" />
              <p className="font-medium text-foreground">{t("login.chatbotBoxTitle")}</p>
            </div>
            <p>{t("login.chatbotBoxDesc")}</p>
            <Link
              to="/supplier/register"
              className="inline-flex items-center justify-center gap-2 mt-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-accent text-foreground transition-colors"
            >
              <span>{t("login.becomeSupplier")}</span>
            </Link>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
